import React, {useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchConversationEvents,
  searchEntitiesInShare,
} from "src/redux/actions/conversation";
import { Conversation } from "src/types/conversations";
import pluralize from "pluralize";
import { DEAL_ROOM_EXPERIENCE } from "src/constants/collections";
import { Asset } from "src/types/assets";
import { Contact } from "src/types/contact";
import dateformat from "dateformat";

import { components } from "react-select";
import { Dropdown, Image } from "react-bootstrap";
import AsyncSelect from "react-select/async";
import { IUseFetchProps, useFetch } from "src/components/hooks/useFetch";
import { FileService } from "src/services/file.service";
import InfiniteScroll from "src/components/infiniteScroll";
import Tooltip from "src/components/tooltip";
import Slider from "src/components/slider";
import { TimeLineLoader } from "../loaders";
import EmptyMessage from "src/components/emptyMessage";
import { RootState } from "src/redux/store";

import styles from "./timeLine.module.scss";
import _ from "lodash";
import cx from "classnames";
import Text from "src/components/text";

interface ITimeLine {
  show: boolean;
  onClose: () => void;
  conversation: Conversation;
}
const TimeLine: React.FC<ITimeLine> = ({ show, onClose, conversation }) => {
  const dispatch = useDispatch();
  const [eventTypes, setEventTypes] = useState([
    { id: 0, value: "all", label: "All Activity" },
    { id: 1, value: "email_open", label: "Email Opened" },
    { id: 2, value: "link_clicked", label: "Link Clicked" },
    { id: 3, value: "asset_download", label: "Asset Download" },
    { id: 4, value: "asset_viewed", label: "Asset Viewed" },
    { id: 5, value: "assets_reshared", label: "Asset Reshared" },
    { id: 6, value: "collection_reshared", label: "Collection Reshared" },
    { id: 7, value: "collection_viewed", label: "Collection Viewed" },
    { id: 8, value: "lead_captured", label: "Lead Captured" },
  ]);

  //selectors
  const { events, pagination, loading, loaded } = useSelector((state: RootState) => {
    const conversationEventsState = _.get(
      state.conversations,
      `conversation.${conversation.id}.events`,
      {},
    );
    const conversationEvents = _.get(conversationEventsState, "items", []);
    const filteredContacts = _.omit(
      _.groupBy(conversationEvents, "contact.id"),
      "undefined",
    );
    const filteredVisitors = _.omit(
      _.groupBy(conversationEvents, "visitor.id"),
      "undefined",
    );
    const contacts = _.map(filteredContacts, (contact, index) => ({
      recipient: index,
      event: contact,
    }));
    const visitors = _.map(filteredVisitors, (visitor, index) => ({
      recipient: index,
      event: visitor,
    }));
    const filteredEvents = _.concat(visitors, contacts);
    return {
      events: filteredEvents,
      pagination: _.get(conversationEventsState, "pagination", {
        currentPage: 0,
        totalPages: 0,
      }),
      loading: _.get(conversationEventsState, "loading", false),
      loaded: _.get(conversationEventsState, "loaded", false),
    };
  });

  //states
  const [selectedEventType, setSelectedEventType] = React.useState<{
    value: string;
    label: string;
  }>(eventTypes[0]);
  const [selectedEntities, setSelectedEntities] = React.useState<
    selectedEntity[]
  >([]);

  //side effects
  useEffect(() => {
    if (conversation.id && !loaded) {
      dispatch(
        fetchConversationEvents({
          request: {
            share: conversation.id,
            eventTypes: [],
            assets: [],
            contacts: [],
          },
          page: 0,
        }),
      );

      if (conversation.viewerType === DEAL_ROOM_EXPERIENCE) {
        const filteredEventTypes = eventTypes.filter(
          (eventType) =>
            eventType.value !== "collection_reshared" &&
            eventType.value !== "collection_viewed");
        setEventTypes([
          ...filteredEventTypes,
          { id: 9, value: "asset_added", label: "Asset Added" },
          { id: 6, value: "deal_room_reshared", label: "Deal Room Reshared" },
          { id: 5, value: "deal_room_viewed", label: "Deal Room Viewed" },
        ]);
      }
    }
  }, []);

  // functions
  const renderName = (contact) => {
    const firstName = _.get(contact, "firstName") || "";
    const lastName = _.get(contact, "lastName") || "";
    return (
      <div className={styles.nameContainer}>
        <div className={styles.avatar}>
          {firstName.charAt(0).toUpperCase() + lastName.charAt(0).toUpperCase()}
        </div>
        <p className={styles.name}>{firstName + " " + lastName}</p>
      </div>
    );
  };

  const renderTimeLine = () => {
    const timeLine: any = [];
    if (_.size(events) === 0) {
      return  <EmptyMessage className={styles.emptyMessage} image={"/images/emptyTimeLine.svg"} message="No record(s) found"/>;
    }

    const getEventDesciption = (event) => {
      const eventType = event.type;
      switch (eventType) {
        case "email_open":
          return <span className={styles.title}>Email opened</span>;
        case "link_clicked":
          return <span className={styles.title}>Clicked on your content</span>;
        case "asset_viewed":
        case "assets_reshared":
        case "asset_download":
        case "asset_added": {
          const asset =
            eventType === "asset_added" ? event.assetAdded : event.target;
          const timeSpent = asset.totalTimeSpent || 0;
          let title = "";
          let timeSpentValue = `~${Math.round(timeSpent)}s`;
          if (timeSpent >= 60) {
            timeSpentValue = `~${Math.round(timeSpent / 60)}m`;
          }
          if (eventType === "asset_viewed") {
            title = "Viewed the asset";
          } else if (eventType === "asset_download") {
            title = "Downloaded the asset";
          } else if (eventType === "assets_reshared") {
            title = "Reshared the asset";
          } else if (eventType === "asset_added") {
            title = "Added the asset";
          }
          return (
            <div className={styles.assetEvents}>
              <span className={styles.title}>{title}</span>
              <Text className={styles.assetName} text={asset.name} />
              <div className={styles.subTitle}>
                <span className={styles.info}>
                  {FileService.getFileType(asset.metadata)}
                </span>
                <span className={styles.info}>
                  {FileService.formatSize(asset.metadata.content_length)}
                </span>
                {eventType !== "asset_added" && (
                  <span className={styles.info}>{timeSpentValue}</span>
                )}
              </div>
            </div>
          );
        }
        case "collection_reshared":
        case "collection_viewed": {
          const title =
            eventType === "collection_reshared"
              ? "Reshared the collection"
              : "Viewed the collection";
          return (
            <div className={styles.assetEvents}>
              <span className={styles.title}>{title}</span>
              <Text className={styles.assetName} text={event.target.name} />
            </div>
          );
        }
        case "deal_room_reshared":
        case "deal_room_viewed": {
          const title =
            eventType === "deal_room_reshared"
              ? "Reshared the deal room"
              : "Viewed the deal room";
          return (
            <div className={styles.assetEvents}>
              <span className={styles.title}>{title}</span>
              <div>
                <Text className={styles.assetName} text={event.target.name} />
              </div>
            </div>
          );
        }
        case "lead_captured":
          return <span className={styles.title}>Captured lead</span>;
        default:
          return "Activity";
      }
    };

    events.forEach((recipientEvent, index) => {
      timeLine.push(
        <div className={styles.timeLineWrapper} key={index}>
          {renderName(
            recipientEvent.event[0].contact || recipientEvent.event[0].visitor,
          )}
          <section className={styles.activities}>
            <div className={styles.activityContainer}>
              {recipientEvent.event.map((event, idx) => {
                if (event.target == null) {
                  return <></>;
                }
                
                return (
                  <div
                    key={idx}
                    className={cx(styles.activity, {
                      [styles.assetViewActivity]: event.type.includes("asset_viewed") || event.type.includes("asset_download") || event.type.includes("asset_added") || event.type.includes("assets_reshared"),
                    })}
                  >
                    <span className={styles.eventDate}>
                      <span className={styles.date}>
                        {dateformat(event.createdDate, "dd mmm yyyy, HH:MM")}
                      </span>
                    </span>
                    <span
                      className={cx(styles.dot)}
                    />
                    <div className={styles.activityContent}>
                      {getEventDesciption(event)}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>,
      );
    });

    return (
      <InfiniteScroll
        hasMore={pagination.currentPage + 1 < pagination.totalPages}
        loading={loading}
        loadMore={() => {
          fetchEvents(
            selectedEntities,
            selectedEventType,
            pagination.currentPage + 1,
          );
        }}
        loader={() => <TimeLineLoader count = {2}/>}
      >
        {timeLine}
      </InfiniteScroll>
    );
  };

  const fetchEvents = (tags: selectedEntity[], selectedEventType, page = 0) => {
    const assets: string[] = [];
    const contacts: string[] = [];
    tags.forEach((tag: selectedEntity) => {
      if (tag.entityType === "asset") {
        assets.push(tag.value);
      } else {
        contacts.push(tag.value);
      }
    });
    dispatch(
      fetchConversationEvents({
        request: {
          share: conversation.id || "",
          eventTypes: [selectedEventType.value],
          assets,
          contacts,
        },
        page,
      }),
    );
  };

  //render
  return (
    <Slider.Dialog
      show={show}
      width="400px"
      className={styles.container}
      onClose={onClose}
    >
      <Slider.Header>
        <div className={styles.header}>
          <div>
            <p className={styles.title}>timeline</p>
            <p className={styles.subTitle}>
              you have shared {conversation.totalAssetsCount}{" "}
              {pluralize("asset", conversation.totalAssetsCount)} with{" "}
              {conversation.totalRecipients}{" "}
              {pluralize("recipient", conversation.totalRecipients)}
            </p>
          </div>
          <i className="paperflite-close" onClick={onClose} />
        </div>
      </Slider.Header>
      <Slider.Body>
        <SelectShareEntity
          conversationId={conversation.id}
          onSelect={(tags: selectedEntity[]) => {
            setSelectedEntities(tags);
            fetchEvents(tags, selectedEventType);
          }}
        />
        <EventTypeDropDown
          options={eventTypes}
          selected={selectedEventType}
          onChange={(value) => {
            setSelectedEventType(value);
            fetchEvents(selectedEntities, value);
          }}
        />
        {renderTimeLine()}
      </Slider.Body>
    </Slider.Dialog>
  );
};

export default TimeLine;

interface IEventTypeDropDown {
  selected: {
    value: string;
    label: string;
  };
  options: Array<{ value: string; label: string; icon?: string }>;
  onChange: ({ value, label }: { value: string; label: string }) => void;
}

const EventTypeDropDown: React.FC<IEventTypeDropDown> = ({
  selected,
  options,
  onChange,
}) => {
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === selected.value);
  }, [selected, options]);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange({
        value,
        label: options.find((option) => option.value === value)?.label || "",
      });
    }
  };

  return (
    <Dropdown className={styles.selectDropdown}>
      <Dropdown.Toggle>
        {selectedOption && <>{selectedOption?.label}</>}

        {!selectedOption}
        {_.size(options) > 1 && <i className="paperflite-dropdown"></i>}
      </Dropdown.Toggle>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
              >
                {selected ? (
                  <i className="paperflite-tick-selected" />
                ) : (
                  <i className="paperflite-radio-button-unselected" />
                )}
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};

interface selectedEntity {
  value: string;
  label: string;
  entityType: string;
  item: Asset | Contact;
}

interface ISelectShareEntity {
  conversationId?: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (tags: selectedEntity[]) => void;
}

const SelectShareEntity: React.FC<ISelectShareEntity> = ({
  conversationId,
  onSelect,
}) => {
  //states
  const [tags, setTags] = React.useState<any>([]);
  const [inputValue, setInputValue] = React.useState("");

  //selectors
  const { loading, succeeded, items, error } = useStoreState(
    `conversations.conversation.${conversationId}.shareEntities`,
  );

  //functions
  const getStyles = () => {
    return {
      placeholder: (base: any, state: any) => ({
        ...base,
        fontSize: "0.75rem",
        display: state.isFocused ? 'none' : 'block',
      }),
      control: (base: any, state: any) => ({
        ...base,
        background: "#fff",
        border: "1px solid #dbdbdb",
        borderRadius: "0.625rem",
        fontSize: "0.875rem",
        paddingLeft: "1.5rem",
        paddingRight: "2.5rem",
        color: "#000",
        fontWeight: "500",
        textTransform: 'none',
        boxShadow: state.isFocused ? 0 : base.boxShadow,
        "&:hover": {
          border: state.isFocused ? "1px solid #dbdbdb" : base.border,
          boxShadow: state.isFocused ? 0 : base.boxShadow,
        },
      }),
      menu: (base: any) => ({
        ...base,
        zIndex: 10,
        backgoundColor: "#fff",
        borderRadius: "0.625rem",
        boxShadow: `0px 0px 16px 0px rgba(18, 18, 18, 0.08)`,
        padding: "1rem",
        textTransform: 'none',
      }),
      multiValue: (base: any) => ({
        ...base,
        padding: "2px 8px",
        fontSize: "0.875rem !important",
        color: "#000",
        fontStyle: "normal",
        fontWeight: "500",
        lineHeight: "1rem",
        borderRadius: "1.25rem",
        background: "#dbdbdb66",
        marginRight: "4px",
        "&:hover": {
          background: "#dbdbdb",
        },
      }),
      multiValueRemove: (base: any) => ({
        ...base,
        color: "#000",
        padding: "0px 5px",
        cursor: "pointer",
        fontSize: "1em",
        lineHeight: "1em",
        "&:hover": {
          background: "#dbdbdb",
        },
      }),
      indicatorsContainer: (base: any) => ({
        ...base,
        display: "none",
      }),
      option: (base: any) => ({
        ...base,
        background: "transparent !important",
        textTransform: 'none',
      }),
      groupHeading: (base: any) => ({
        ...base,
        textTransform: 'none',
      })
    };
  };

  const useFetchProps: IUseFetchProps = {
    action: searchEntitiesInShare,
    loading,
    succeeded,
    response: items,
    error,
  };

  const { handleFetch } = useFetch(useFetchProps);
  const loadOptions = (queryString) => {
    if (!queryString || queryString.trim() === "") {
      return Promise.resolve([]);
    }
    try {
      const requestBody = {
        query: queryString,
        filter: null,
        page: 0,
        limit: 15,
        highlight: false,
        shareId: conversationId,
      };
      return handleFetch(requestBody)?.then((results: any[]) => {
        const options = results.map((item) => {
          return {
            options: item.options,
            label: item.label,
          };
        });
        if(options.length === 0) {
          return [{ label: "No results found", options: [] }];
        }
        return options;
      });
    } catch (e) {
      console.error(e);
    }
  };

  const CustomMultiValueRemove = (props) => {
    return (
      <components.MultiValueRemove {...props}>
        <i className={cx("paperflite-close", styles.selectCloseButton)}></i>
      </components.MultiValueRemove>
    );
  };
  const CustomOption = (props: any) => {
    const icon = _.get(props.data, "item.icon.thumbnail");
    let name = "";

    if (props.data.item.entityType === "asset") {
      name = props.label.charAt(0).toUpperCase();
    } else {
      name = `${(_.get(props, "data.item.firstName") || "").charAt(0)}${(_.get(props, "data.item.lastName") || "").charAt(0)}`;
    }

    return (
      <components.Option {...props}>
        <div
          className={cx(styles.option, {
            [styles.assetOption]: props.data.item.entityType === "asset",
            [styles.contactOption]: props.data.item.entityType === "contact",
          })}
        >
          {icon && (
            <Image
              src={props.data.item.icon.thumbnail}
              className={styles.avatar}
            />
          )}
          {!icon && <span className={styles.avatar}>{name.toUpperCase()}</span>}
          <span className={styles.label}>{props.label}</span>
        </div>
      </components.Option>
    );
  };

  return (
    <div className={styles.searchContainer}>
      <i className={cx(`paperflite-search`, styles.search)} />
      <AsyncSelect
        className={styles.searchInput}
        isMulti
        isClearable={true}
        backspaceRemovesValue
        placeholder="search recipients and assets..."
        filterOption={() => {
          return true;
        }}
        inputValue={inputValue}
        onInputChange={(value, action) => {
          if(action.action === "input-change") {
            setInputValue(value)
          }
        }}
        styles={getStyles()}
        onChange={(newtags) => {
          setInputValue('');
          setTags(newtags as { value: string; label: string }[]);
          onSelect(newtags as selectedEntity[]);
        }}
        value={tags}
        openMenuOnClick={false}
        loadOptions={loadOptions}
        loadingMessage={() => `Loading...`}
        noOptionsMessage={() => `No results found`}
        openMenuOnFocus={false}
        components={{
          MultiValueRemove: CustomMultiValueRemove,
          Option: CustomOption,
        }}
      />
      {inputValue && <i className={cx(`paperflite-close`, styles.close)} onClick={() => setInputValue('')}/>}
    </div>
  );
};

interface IReduxState {
  loading: boolean;
  succeeded: boolean;
  items?: any;
  error: any;
}
const useStoreState = (path: string): IReduxState => {
  const reduxData = useSelector((state: any) => {
    const entityTypeData = _.get(state, `${path}`, {});
    let items = _.get(entityTypeData, "items", {});
    let assetoptions = [];
    let recipientOptions = [];

    if (items) {
      if(items.assets) {
        assetoptions = items.assets.map((item) => {
          return {
            value: item.id,
            label: item.name,
            entityType: "asset",
            item,
          };
        });
      }
      if(items.contacts) {
      recipientOptions = items.contacts.map((item) => {
        return {
          value: item.id,
          label: item.firstName,
          entityType: "contact",
          item,
        };
      });
    }
    }

    items = [
      { label: "Recipients", options: recipientOptions },
      { label: "Assets", options: assetoptions },
    ];

    return {
      loading: _.get(entityTypeData, "loading", false),
      succeeded: _.get(entityTypeData, "succeeded", false),
      items,
      error: _.get(entityTypeData, "error", null),
      pagination: _.get(entityTypeData, "pagination", {}),
    };
  }, _.isEqual);

  return reduxData;
};
